import React, { useState, useEffect } from "react";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogTitle from "@mui/material/DialogTitle";
import Tabs from "@mui/material/Tabs";
import Tab from "@mui/material/Tab";
import Button from "@mui/material/Button";
import Divider from "@mui/material/Divider";
import { CircularProgress } from "@mui/material";
import { useTranslation } from "react-i18next";
import { getMLRstatus } from "../../services/apis";
import { emitter } from "../../Uitls/Util";
import "../../styles/pages/Banners/BannerManager.scss";
import BannerInformationTab from "./BannerInformationTab";
import SourceBannerInformationTab from "./SourceBannerInformationTab";
import CreatedPublishInformationTab from "./CreatedPublishInformationTab";

/* --------------------------------- */
/* Main Modal                        */
/* --------------------------------- */

export default function BannerAndTemplateDetailsModal(props) {
  const {
    setShowDetails,
    showDetails,
    bannerDetails,
    setBannerDetails,
    recentlyViewedData,
    loading: externalLoading = false,
    currentSize,
  } = props;

  const { t } = useTranslation();
  const [mlrLoading, setMlrLoading] = useState(false);
  const [tab, setTab] = useState(0);
  const normalizeDocId = (docId) => {
    if (docId === null || docId === undefined) return "";
    const normalized = String(docId).trim();
    return normalized && normalized !== "-" ? normalized : "";
  };
  const isSameDocId = (a, b) => {
    const normalizedA = normalizeDocId(a);
    const normalizedB = normalizeDocId(b);
    return !!normalizedA && !!normalizedB && normalizedA === normalizedB;
  };

  const handleClose = () => {
    setShowDetails(false);
    setBannerDetails({});
    setTab(0);
  };
  const getLatestMLRStatus = (damDocNumber, targetKey = "reviewStatus") => {
    const resolvedDamDocNumber = normalizeDocId(damDocNumber);
    if (!resolvedDamDocNumber) return;
    setMlrLoading(true);

    getMLRstatus(resolvedDamDocNumber, async (response, err) => {
      setMlrLoading(false);
      if (err) return;

      const data = await response.data;
      const status = data?.mlrStatus || "Not Submitted";

      setBannerDetails((prev) => ({
        ...prev,
        [targetKey]: status,
      }));
    });
  };

  useEffect(() => {
    if (normalizeDocId(bannerDetails?.veevaDocNumber)) {
      getLatestMLRStatus(bannerDetails.veevaDocNumber, "reviewStatus");
    } else if (normalizeDocId(recentlyViewedData?.damDocNumber)) {
      getLatestMLRStatus(recentlyViewedData?.damDocNumber, "reviewStatus");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerDetails?.veevaDocNumber, recentlyViewedData?.damDocNumber]);

  useEffect(() => {
    if (normalizeDocId(bannerDetails?.sourceVeevaDocNumber)) {
      getLatestMLRStatus(
        bannerDetails.sourceVeevaDocNumber,
        "sourceReviewStatus",
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerDetails?.sourceVeevaDocNumber]);

  useEffect(() => {
    if (normalizeDocId(bannerDetails?.rsdDocNumber)) {
      getLatestMLRStatus(bannerDetails.rsdDocNumber, "rsdReviewStatus");
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [bannerDetails?.rsdDocNumber]);

  useEffect(() => {
    const handleMLRStatusUpdate = (data) => {
      const eventDocId = normalizeDocId(data?.damDocNumber);
      if (!eventDocId) return;

      const assetDocId =
        normalizeDocId(bannerDetails?.veevaDocNumber) ||
        normalizeDocId(recentlyViewedData?.damDocNumber);
      const sourceDocId = normalizeDocId(bannerDetails?.sourceVeevaDocNumber);
      const rsdDocId = normalizeDocId(bannerDetails?.rsdDocNumber);

      if (isSameDocId(eventDocId, assetDocId)) {
        setBannerDetails((prev) => ({
          ...prev,
          reviewStatus: data.mlrStatus || "Not Submitted",
        }));
      }

      if (isSameDocId(eventDocId, sourceDocId)) {
        setBannerDetails((prev) => ({
          ...prev,
          sourceReviewStatus: data.mlrStatus || "Not Submitted",
        }));
      }

      if (isSameDocId(eventDocId, rsdDocId)) {
        setBannerDetails((prev) => ({
          ...prev,
          rsdReviewStatus: data.mlrStatus || "Not Submitted",
        }));
      }
    };

    emitter.on("autoSync", handleMLRStatusUpdate);
    return () => {
      emitter.off("autoSync", handleMLRStatusUpdate);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    bannerDetails?.veevaDocNumber,
    bannerDetails?.sourceVeevaDocNumber,
    bannerDetails?.rsdDocNumber,
    recentlyViewedData?.damDocNumber,
  ]);

  const isLoading = externalLoading || mlrLoading;
  return (
    <Dialog
      disableBackdropClick
      open={showDetails}
      onClose={handleClose}
      className="details-modal detilsDialog"
      id="details-modal"
      aria-labelledby="form-dialog-title"
      PaperProps={{ className: "details-dialog-paper" }}
    >
      {isLoading && (
        <>
          <div className="modal-backdrop fade show" />
          <CircularProgress size="30px" className="customLoader" />
        </>
      )}

      <div className="details-shell">
        <div className="details-header">
          <DialogTitle id="form-dialog-title" className="details-title">
            {t("DETAILS")}
            {/* {bannerDetails?.oneTimeAsset && (
              <div className="onetimeTag"> One-time content</div>
            )} */}
          </DialogTitle>
          <Divider className="dividerline" />
          <Tabs
            value={tab}
            onChange={(_, v) => setTab(v)}
            indicatorColor="primary"
            textColor="primary"
            className="details-tabs"
          >
            <Tab label={t("Banner Information")} />
            <Tab label={t("Source Banner Information")} />
            <Tab label={t("Created & Publish Information")} />
          </Tabs>
        </div>

        <Divider />

        <div className="details-scroll">
          {tab === 0 && (
            <BannerInformationTab
              bannerDetails={bannerDetails}
              recentlyViewedData={recentlyViewedData}
              onRefreshMLR={getLatestMLRStatus}
              currentSize={currentSize}
            />
          )}

          {tab === 1 && (
            <SourceBannerInformationTab
              bannerDetails={bannerDetails}
              recentlyViewedData={recentlyViewedData}
              onRefreshMLR={getLatestMLRStatus}
            />
          )}

          {tab === 2 && (
            <CreatedPublishInformationTab bannerDetails={bannerDetails} />
          )}
        </div>

        <DialogActions className="details-footer">
          <Button
            onClick={handleClose}
            className="close-pill"
            disableElevation
            size="small"
          >
            CLOSE
          </Button>
        </DialogActions>
      </div>
    </Dialog>
  );
}
